import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, TrendingUp, Users, Send, Target, Award, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const TABS = [
  { k: "traffic", label: "Traffic", icon: TrendingUp },
  { k: "funnel", label: "Funnel", icon: Target },
  { k: "drip", label: "Drip", icon: Send },
  { k: "content_roi", label: "Content ROI", icon: BarChart3 },
  { k: "referrals", label: "Referrals", icon: Award },
];

export default function AdminMarketingDashboards() {
  const [tab, setTab] = useState("traffic");
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setData(null);
    try {
      const endpoint = tab.replace("_", "-");
      const r = await api.get(`/marketing/dashboards/${endpoint}`);
      setData(r.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { api.get("/marketing/dashboards/summary").then(r => setSummary(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-mkt-dashboards">
      <div>
        <div className="overline text-secondary">Admin · Marketing Analytics</div>
        <h1 className="mt-1 font-display text-4xl font-light tracking-tight flex items-center gap-3">
          <BarChart3 className="w-7 h-7 text-primary" /> Inbound Marketing Dashboards
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Traffic · Funnel · Drip · Content ROI · Referrals</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label="Pages live" value={summary.seo_pages_published} icon={TrendingUp} />
          <Tile label="In review queue" value={summary.seo_pages_pending} color="text-amber-600" />
          <Tile label="Leads captured" value={summary.leads_all_time} icon={Users} />
          <Tile label="Drips active" value={summary.drip_active} color="text-emerald-600" icon={Send} />
          <Tile label="Drips completed" value={summary.drip_completed} />
        </div>
      )}

      <div className="border-b border-border">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`mkt-tab-${t.k}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 shrink-0 ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-12"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

      {!loading && data && tab === "traffic" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Total views" value={data.totals.views} />
            <Tile label="Total leads" value={data.totals.leads} color="text-emerald-600" />
            {data.by_category.slice(0, 2).map(c => (
              <Tile key={c.category} label={`${c.category.replace("_", " ")} views`} value={c.views} />
            ))}
          </div>
          <div>
            <h3 className="font-medium mb-3">By category</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.by_category.map(c => (
                <div key={c.category} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium capitalize">{c.category.replace("_", " ")}</div>
                    <div className="text-xs text-muted-foreground">{c.pages} pages</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl">{c.views}</div>
                    <div className="text-xs text-muted-foreground">{c.leads} leads</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-medium mb-3">Top landing pages</h3>
            <div className="rounded-xl border border-border bg-card divide-y divide-border">
              {data.top_pages.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No published pages yet.</div>}
              {data.top_pages.map(p => (
                <div key={p.slug} className="p-3 flex items-center gap-4 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{p.title}</div>
                    <div className="text-xs text-muted-foreground">{p.category.replace("_", " ")} · /{p.slug}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold">{p.views}</div>
                    <div className="text-[10px] text-muted-foreground">{p.leads_captured} leads</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && data && tab === "funnel" && (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-6">
            {data.stages.map((s, i) => (
              <div key={i} className="flex items-center gap-4 py-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">{i + 1}</div>
                <div className="flex-1">
                  <div className="font-medium">{s.label}</div>
                  <div className="text-xs text-muted-foreground">{s.sub}</div>
                </div>
                <div className="font-display text-3xl font-semibold">{s.value}</div>
              </div>
            ))}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-xs text-muted-foreground">End-to-end conversion</div>
              <div className="font-display text-4xl text-primary">{data.conversion_rate_pct}%</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Window: last {data.period_days} days.</p>
        </div>
      )}

      {!loading && data && tab === "drip" && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Sequence</th>
                <th className="text-right p-3">Steps</th>
                <th className="text-right p-3">Active</th>
                <th className="text-right p-3">Completed</th>
                <th className="text-right p-3">Sends</th>
                <th className="text-right p-3">Completion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.sequences.map(s => (
                <tr key={s.id}>
                  <td className="p-3">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[10px] text-muted-foreground">{s.trigger}</div>
                  </td>
                  <td className="text-right p-3">{s.steps}</td>
                  <td className="text-right p-3 text-emerald-600">{s.active}</td>
                  <td className="text-right p-3">{s.completed}</td>
                  <td className="text-right p-3">{s.sends}</td>
                  <td className="text-right p-3">{s.completion_rate_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && tab === "content_roi" && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Page</th>
                <th className="text-right p-3">Views</th>
                <th className="text-right p-3">Leads</th>
                <th className="text-right p-3">Conv %</th>
                <th className="text-right p-3">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.pages.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-muted-foreground">No content yet.</td></tr>}
              {data.pages.map(p => (
                <tr key={p.id}>
                  <td className="p-3">
                    <div className="font-medium line-clamp-1">{p.title}</div>
                    <div className="text-[10px] text-muted-foreground">{p.category.replace("_", " ")}</div>
                  </td>
                  <td className="text-right p-3">{p.views}</td>
                  <td className="text-right p-3 text-emerald-600">{p.leads_captured}</td>
                  <td className="text-right p-3">{p.lead_conversion_pct}%</td>
                  <td className="text-right p-3"><Badge variant="outline">{p.confidence_score}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && tab === "referrals" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Tile label="Total referrals" value={data.totals.referrals} />
            <Tile label="Redeemed" value={data.totals.redeemed} color="text-emerald-600" />
            <Tile label="Conversion" value={`${data.totals.conversion_pct}%`} />
          </div>
          <div className="rounded-xl border border-border bg-card">
            <div className="p-3 font-medium border-b border-border">Top referrers</div>
            {data.top_referrers.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No referrals data yet.</div>}
            {data.top_referrers.map(r => (
              <div key={r.email} className="p-3 flex justify-between text-sm border-b border-border last:border-b-0">
                <span>{r.email}</span>
                <span className="font-semibold">{r.referrals}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, color = "", icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-1 font-display text-3xl font-semibold ${color}`}>{value ?? 0}</div>
    </div>
  );
}
